import * as yup from 'yup';
import { ArrowUpRight, ExternalLink } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';

import { Event } from '../Settings/helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { editorOptions } from '../../../../common/Tiptap/helpers';
import { getServerErrors } from '../../../../common/helpers';
import { replacementKeys } from '../Settings/EditEventRegistrationForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import DateRangeForm from '../../../Common/DateRangeForm';
import GetEventSettingsQuery from '../Settings/GetEventSettingsQuery';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import config from '../../../config';
import useEditEventMutation from '../Settings/useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

export interface ResaleSettingsPageProps {
  eventId: string;
}

const ResaleSettingsPage = ({ eventId }: ResaleSettingsPageProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const device = useMediaDevice();

  const { data } = useQuery(GetEventSettingsQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
  });

  const event = data?.project.event;

  if (!event) {
    return <UI.PageLoader />;
  }

  const stats = (
    <>
      {event.enable_public_resale && (
        <UI.Span sc={{ color: 'gray.600' }}>
          {t('n_tickets_available', {
            value: formatNumber(event.cached_registrations_for_sale_count),
            count: event.cached_registrations_for_sale_count,
          })}
        </UI.Span>
      )}
      {(event.enable_public_resale || event.sold_registrations_count > 0) && (
        <UI.Span sc={{ color: 'gray.600' }}>
          {t('n_tickets_sold', {
            value: formatNumber(event.sold_registrations_count),
            count: event.sold_registrations_count,
          })}
        </UI.Span>
      )}
    </>
  );

  return (
    <UI.Section>
      <UI.FormGrid>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.SectionHeading>
            {t('resale')}
          </UI.SectionHeading>

          <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 3 }}>
            {!device.isSmall && stats}

            <UI.A
              href={event.resale_url}
              target="_blank"
              rel="noopener noreferer"
              sc={{ fontWeight: 600 }}
            >
              {t('public_resale_page')}
              {' '}
              <UI.Icon>
                <ArrowUpRight />
              </UI.Icon>
            </UI.A>
          </UI.FlexContainer>
        </UI.FlexContainer>

        {device.isSmall && (
          <UI.FlexContainer sc={{ alignItems: 'center', spaceX: 3 }}>
            {stats}
          </UI.FlexContainer>
        )}

        <ResaleSettingsForm event={event} />
      </UI.FormGrid>
    </UI.Section>
  );
};

const editResaleSettingsSchema = yup.object();

export interface ResaleSettingsFormProps {
  event: Event;
}

const ResaleSettingsForm = ({ event }: ResaleSettingsFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const { formatCurrency, locale } = useLocale();

  const resellableTickets = event.tickets.filter((ticket) => ticket.allowable_for_resale);
  const resellableProducts = event.products.filter((product) => product.allowable_for_resale);

  const getValues = (event: Event) => ({
    enable_private_resale: event.enable_private_resale,
    private_resale_start_date: event.private_resale_start_date,
    private_resale_end_date: event.private_resale_end_date,
    enable_public_resale: event.enable_public_resale,
    public_resale_start_date: event.public_resale_start_date,
    public_resale_end_date: event.public_resale_end_date,
    resale_description: event.resale_description,
    resale_description_translations: translationsForAttribute(event.translations, 'resale_description'),
    enter_custom_confirmation_text: !!event.resale_confirmation_text,
    confirmation_text: event.resale_confirmation_text,
    confirmation_text_translations: translationsForAttribute(event.translations, 'resale_confirmation_text'),
    enter_custom_confirmation_mail: !!event.resale_confirmation_mail_text,
    confirmation_mail_text: event.resale_confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(event.translations, 'resale_confirmation_mail_text'),
    ticket_listed_mail_text: event.resale_ticket_listed_mail_text,
    ticket_listed_mail_text_translations: translationsForAttribute(
      event.translations,
      'resale_ticket_listed_mail_text',
    ),
    resellable_tickets: resellableTickets.map((ticket) => ({
      id: ticket.id,
      allow_resale: ticket.allow_resale,
      resale_promotion_id: ticket.resale_promotion?.id || null,
    })),
    resellable_products: resellableProducts.map((product) => ({
      id: product.id,
      allow_resale: product.allow_resale,
    })),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const toggleCustomConfirmationText = () => {
    form.set((values) => ({ ...values, enter_custom_confirmation_text: !values.enter_custom_confirmation_text }));
  };

  const toggleCustomConfirmationMail = () => {
    form.set((values) => ({ ...values, enter_custom_confirmation_mail: !values.enter_custom_confirmation_mail }));
  };

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      resale: {
        enable_private_resale: form.values.enable_private_resale,
        private_resale_start_date: form.values.private_resale_start_date,
        private_resale_end_date: form.values.private_resale_end_date,
        enable_public_resale: form.values.enable_public_resale,
        public_resale_start_date: form.values.public_resale_start_date,
        public_resale_end_date: form.values.public_resale_end_date,
        description: form.values.resale_description,
        description_translations: form.values.resale_description_translations,
        confirmation_text: form.values.enter_custom_confirmation_text ? form.values.confirmation_text : null,
        confirmation_text_translations: form.values.enter_custom_confirmation_text
          ? form.values.confirmation_text_translations : [],
        confirmation_mail_text: form.values.enter_custom_confirmation_mail ? form.values.confirmation_mail_text : null,
        confirmation_mail_text_translations: form.values.enter_custom_confirmation_mail
          ? form.values.confirmation_mail_text_translations : [],
        ticket_listed_mail_text: form.values.ticket_listed_mail_text,
        ticket_listed_mail_text_translations: form.values.ticket_listed_mail_text_translations,
        resellable_tickets: form.values.resellable_tickets,
        resellable_products: form.values.resellable_products,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editResaleSettingsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.Card sc={{ spacing: 'xl' }}>
        <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
          <UI.Nav sc={{ pr: [0, 6] }}>
            <UI.Div>
              <UI.H4>
                {t('ticket_resale')}
              </UI.H4>
              <UI.InputDescription sc={{ mb: 1 }}>
                {t('resale_description')}
              </UI.InputDescription>
              <UI.InputDescription>
                <UI.A
                  href={config.resale.infoUrl[locale]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('about_resale')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
            </UI.Div>
          </UI.Nav>

          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.Legend>
              {t('public_resale')}
            </UI.Legend>

            <UI.InputGroup
              sc={{
                valid: !errors.enable_public_resale,
                touched: form.touched.enable_public_resale,
              }}
            >
              <UI.Checkbox
                {...form.checkbox('enable_public_resale')}
                checked={form.values.enable_public_resale}
                sc={{ box: true, block: true }}
              >
                <UI.Strong sc={{ block: true }}>
                  {t('enable_public_resale_label')}
                </UI.Strong>
                {t('enable_public_resale_description')}
              </UI.Checkbox>
              <UI.ErrorMessages attribute={t('enable_ticket_resale')} errors={errors.enable_public_resale} />
            </UI.InputGroup>

            <DateRangeForm
              form={form}
              errors={errors}
              keys={{
                startDate: 'public_resale_start_date',
                startTime: 'public_resale_start_time',
                endDate: 'public_resale_end_date',
                endTime: 'public_resale_end_time',
              }}
            />

            <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
              <UI.InputLabel id="ResaleDescriptionLabel">
                {t('description')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_description_description')}
              </UI.InputDescription>
              <TranslatableEditor
                form={form}
                errors={errors}
                name="resale_description"
                defaultLocale={event.locale}
                title={t('description')}
                attributes={{
                  'aria-labelledby': 'ResaleDescriptionLabel',
                }}
              />
              <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
            </UI.InputGroup>

            <UI.InputGroup>
              <UI.InputLabel>
                {t(event.enable_waitlist ? 'waitlist_on' : 'waitlist_off')}
              </UI.InputLabel>
              <UI.InputDescription>
                <Trans i18nKey="resale_waitlist_description">
                  <UI.Link to={`/${project.id}/events/${event.id}/settings/waitlist`} />
                </Trans>
              </UI.InputDescription>
            </UI.InputGroup>

            <UI.HR />

            <UI.Legend>
              {t('private_resale')}
            </UI.Legend>

            <UI.InputGroup
              sc={{
                valid: !errors.enable_private_resale,
                touched: form.touched.enable_private_resale,
              }}
            >
              <UI.Checkbox
                {...form.checkbox('enable_private_resale')}
                sc={{ box: true, block: true }}
              >
                <UI.Strong sc={{ block: true }}>
                  {t('enable_private_resale_label')}
                </UI.Strong>
                {t('enable_private_resale_description')}
              </UI.Checkbox>
              <UI.ErrorMessages attribute={t('enable_ticket_resale')} errors={errors.enable_private_resale} />
            </UI.InputGroup>

            <DateRangeForm
              form={form}
              errors={errors}
              keys={{
                startDate: 'private_resale_start_date',
                startTime: 'private_resale_start_time',
                endDate: 'private_resale_end_date',
                endTime: 'private_resale_end_time',
              }}
            />

            <UI.HR />

            <UI.Legend>
              {t('tickets_and_products')}
            </UI.Legend>

            <UI.InputGroup sc={{ valid: !errors.resellable_tickets, touched: form.touched.resellable_tickets }}>
              <UI.InputLabel>
                {t('resellable_tickets')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resellable_tickets_description')}
              </UI.InputDescription>
              <UI.FormTable>
                <UI.TBody>
                  {resellableTickets.map((ticket, index) => (
                    <UI.TR key={ticket.id}>
                      <UI.TD style={{ minWidth: 0, width: '50%' }}>
                        <UI.Checkbox
                          {...form.checkbox(`resellable_tickets.${index}.allow_resale`)}
                        >
                          {ticket.title}
                        </UI.Checkbox>
                      </UI.TD>
                      <UI.TD>
                        <UI.Select
                          {...form.select(`resellable_tickets.${index}.resale_promotion_id`)}
                          sc={{
                            placeholder: !form.get(`resellable_tickets.${index}.allow_resale`),
                          }}
                          value={form.get(`resellable_tickets.${index}.resale_promotion_id`) ?? ''}
                        >
                          <option value="">
                            {t('keep_original_rate')}
                          </option>
                          {ticket.promotions.map((promotion) => (
                            <option value={promotion.id} key={promotion.id}>
                              {promotion.title || t('standard')}
                              {' • '}
                              {formatCurrency(promotion.amount)}
                            </option>
                          ))}
                        </UI.Select>
                      </UI.TD>
                    </UI.TR>
                  ))}
                </UI.TBody>
              </UI.FormTable>
              {resellableTickets.length === 0 && (
                <UI.Div sc={{ color: 'gray.500', mt: 2 }}>
                  <Trans i18nKey="no_tickets_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/tickets`} />
                  </Trans>
                </UI.Div>
              )}
              <UI.ErrorMessages attribute={t('resellable_tickets')} errors={errors.resellable_tickets} />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.resellable_products, touched: form.touched.resellable_products }}>
              <UI.InputLabel>
                {t('resellable_products')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resellable_products_description')}
              </UI.InputDescription>
              {resellableProducts.map((product, index) => (
                <UI.Checkbox
                  {...form.checkbox(`resellable_products.${index}.allow_resale`)}
                  key={product.id}
                >
                  {product.title}
                </UI.Checkbox>
              ))}
              {resellableProducts.length === 0 && (
                <UI.Div sc={{ color: 'gray.500', mt: 2 }}>
                  <Trans i18nKey="no_products_created">
                    <UI.Link to={`/${project.id}/events/${event.id}/products/add`} />
                  </Trans>
                </UI.Div>
              )}
              <UI.ErrorMessages attribute={t('resellable_products')} errors={errors.resellable_products} />
            </UI.InputGroup>

            <UI.HR />

            <UI.Legend>
              {t('confirmation_texts')}
            </UI.Legend>

            <UI.InputGroup
              sc={{ valid: !errors.confirmation_text, touched: form.touched.confirmation_text }}
            >
              <UI.InputLabel id="ResaleConfirmationTextLabel">
                {t('resale_confirmation_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_confirmation_text_description')}
              </UI.InputDescription>
              <UI.Checkbox
                checked={form.values.enter_custom_confirmation_text}
                onChange={() => toggleCustomConfirmationText()}
              >
                {t('enter_custom_confirmation_text')}
              </UI.Checkbox>
              {form.values.enter_custom_confirmation_text && (
                <UI.Div sc={{ mt: 2 }}>
                  <TranslatableEditor
                    form={form}
                    errors={errors}
                    name="confirmation_text"
                    defaultLocale={event.locale}
                    title={t('confirmation_text')}
                    attributes={{
                      'aria-labelledby': 'ResaleConfirmationTextLabel',
                    }}
                  />
                </UI.Div>
              )}
              <UI.ErrorMessages
                attribute={t('confirmation_text')}
                errors={errors.confirmation_text}
              />
            </UI.InputGroup>

            <UI.InputGroup
              sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
            >
              <UI.InputLabel id="ResaleConfirmationMailTextLabel">
                {t('resale_confirmation_mail_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_confirmation_mail_text_description')}
              </UI.InputDescription>
              <UI.Checkbox
                checked={form.values.enter_custom_confirmation_mail}
                onChange={() => toggleCustomConfirmationMail()}
              >
                {t('enter_custom_confirmation_text')}
              </UI.Checkbox>
              {form.values.enter_custom_confirmation_mail && (
                <UI.Div sc={{ mt: 2 }}>
                  <MailEditorLayout
                    editor={(props) => (
                      <TranslatableEditor
                        form={form}
                        errors={errors}
                        name="confirmation_mail_text"
                        title={t('resale_confirmation_mail_text')}
                        defaultLocale={event.locale}
                        options={editorOptions.full}
                        variables={replacementKeys}
                        attributes={{
                          'aria-labelledby': 'ResaleConfirmationMailTextLabel',
                        }}
                        {...props}
                      />
                    )}
                    value={form.values.confirmation_mail_text}
                    event={event}
                    showRegistrationSummary
                    showOrderSummary
                  />
                </UI.Div>
              )}
              <UI.ErrorMessages
                attribute={t('resale_confirmation_mail_text')}
                errors={errors.confirmation_mail_text}
              />
            </UI.InputGroup>

            <UI.InputGroup
              sc={{ valid: !errors.ticket_listed_mail_text, touched: form.touched.ticket_listed_mail_text }}
            >
              <UI.InputLabel id="ResaleTicketListedMailTextLabel">
                {t('resale_ticket_listed_mail_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('resale_ticket_listed_mail_text_description')}
              </UI.InputDescription>
              <UI.Div sc={{ mt: 2 }}>
                <MailEditorLayout
                  editor={(props) => (
                    <TranslatableEditor
                      form={form}
                      errors={errors}
                      name="ticket_listed_mail_text"
                      defaultLocale={event.locale}
                      title={t('resale_ticket_listed_mail_text')}
                      options={editorOptions.full}
                      variables={replacementKeys}
                      attributes={{
                        'aria-labelledby': 'ResaleTicketListedMailTextLabel',
                      }}
                      {...props}
                    />
                  )}
                  value={form.values.ticket_listed_mail_text}
                  event={event}
                  showRegistrationSummary
                />
              </UI.Div>
              <UI.ErrorMessages
                attribute={t('resale_ticket_listed_mail_text')}
                errors={errors.ticket_listed_mail_text}
              />
            </UI.InputGroup>

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save_event')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.FormGrid>
      </UI.Card>
    </UI.Form>
  );
};

export default ResaleSettingsPage;
